
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, mergeMap } from 'rxjs/operators';
import { SelfDashboardService } from '../../service/self-dashboard.service';
import { SelfDashboardActions } from './self-dashboard.actions';
import { of } from 'rxjs';

/**
 * Effects for Self Dashboard state management
 * Handles async operations for loading self dashboard data
 */
@Injectable()
export class SelfDashboardEffects {

  updateAttestationNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelfDashboardActions.updateAttestationNotes),
      switchMap(({ userAccountsId, notes, campaignId, parameters }) =>
        this.selfDashboardService.updateAttestationNotes(userAccountsId, notes, campaignId).pipe(
          switchMap((attestation) => [
            SelfDashboardActions.updateAttestationNotesSuccess({ attestation }),
            SelfDashboardActions.loadAllSelfDashboard({ campaignId, parameters })
          ]),
          catchError((error) =>
            of(SelfDashboardActions.updateAttestationNotesFailure({ error }))
          )
        )
      )
    )
  );

  exportKirs$ = createEffect(
    (ks: SelfDashboardService = inject(SelfDashboardService)) => {
      return this.actions$.pipe(
        ofType(SelfDashboardActions.exportAttestationTemplate),
        mergeMap(({ campaignId, parameters, timeZone }) =>
          ks.exportAttestationTemplate(campaignId, timeZone, parameters).pipe(
            map((file) => {
              // Download diretto del file (se presente)
              if (file?.data && file.data.length > 0) {
                const f = file.data[0];
                const byteChars = atob(f!.content);
                const byteNumbers = new Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) {
                  byteNumbers[i] = byteChars.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const link = window.document.createElement("a");
                link.href = window.URL.createObjectURL(blob);
                link.download = f!.filename ?? "attestation_template.xlsx";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
              }
              return SelfDashboardActions.exportAttestationTemplateSuccess({
                attestationTemplate: file
              });
            }),
            catchError((error) => of(SelfDashboardActions.exportAttestationTemplateFailure({ error })))
          )
        )
      )
    }
  );



  loadSelfDashboard$ = createEffect(
    (selfDashboardService: SelfDashboardService = inject(SelfDashboardService)) => {
      return this.actions$.pipe(
        ofType(SelfDashboardActions.loadSelfDashboard),
        switchMap(({ parameters, campaignId }) =>
          selfDashboardService.getSelfDashboardDatas(campaignId, parameters).pipe(
            map((response) =>
              SelfDashboardActions.loadSelfDashboardSuccess({
                selfDashboard: response

              })
            ),
            catchError((error) => {

              return of(SelfDashboardActions.loadSelfDashboardFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  loadAllSelfDashboard$ = createEffect(
    (selfDashboardService: SelfDashboardService = inject(SelfDashboardService)) => {
      return this.actions$.pipe(
        ofType(SelfDashboardActions.loadAllSelfDashboard),
        switchMap(({ parameters, campaignId }) =>
          selfDashboardService.getSelfDashboardDatas(campaignId, parameters).pipe(
            map((response) =>
              SelfDashboardActions.loadAllSelfDashboardSuccess({
                selfDashboard: response

              })
            ),
            catchError((error) => {

              return of(SelfDashboardActions.loadAllSelfDashboardFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  // exportAttestationTemplate$ = createEffect(
  //   (selfDashboardService: SelfDashboardService = inject(SelfDashboardService)) => {
  //     return this.actions$.pipe(
  //       ofType(SelfDashboardActions.exportAttestationTemplate),
  //       mergeMap(({ campaignId, timeZone, parameters }) =>
  //         selfDashboardService.exportAttestationTemplate(campaignId, timeZone, parameters).pipe(
  //           map((file) => {
  //             // Download diretto del file (se presente)
  //             if (file?.data && file.data.length > 0) {
  //               const f = file.data[0];
  //               const byteChars = atob(f.content);
  //               const byteNumbers = new Array(byteChars.length);
  //               for (let i = 0; i < byteChars.length; i++) {
  //                 byteNumbers[i] = byteChars.charCodeAt(i);
  //               }
  //               const byteArray = new Uint8Array(byteNumbers);
  //               const blob = new Blob([byteArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  //               const link = window.document.createElement("a");
  //               link.href = window.URL.createObjectURL(blob);
  //               link.download = f.filename ?? "attestation_template.xlsx";
  //               document.body.appendChild(link);
  //               link.click();
  //               document.body.removeChild(link);
  //               window.URL.revokeObjectURL(link.href);
  //             }
  //             return SelfDashboardActions.exportAttestationTemplateSuccess({
  //               attestationTemplate: file
  //             });
  //           }),
  //           catchError((error) => of(SelfDashboardActions.exportAttestationTemplateFailure({ error })))
  //         )
  //       )
  //     )
  //   }
  // );

  loadCampaigns$ = createEffect(
    (selfDashboardService: SelfDashboardService = inject(SelfDashboardService)) => {
      return this.actions$.pipe(
        ofType(SelfDashboardActions.loadCampaigns),
        switchMap(() =>
          selfDashboardService.getAdherenceCampaigns().pipe(
            map((response) =>
              SelfDashboardActions.loadCampaignsSuccess({
                campaigns: response
              })
            ),
            catchError((error) => {

              return of(SelfDashboardActions.loadCampaignsFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  createOrUpdateCampaign$ = createEffect(
    (selfDashboardService: SelfDashboardService = inject(SelfDashboardService)) => {
      return this.actions$.pipe(
        ofType(SelfDashboardActions.createOrUpdateCampaign),
        switchMap(({ id, year }) =>
          selfDashboardService.createOrUpdateAdherenceCampaign(id, year).pipe(
            map((response) =>
              SelfDashboardActions.createOrUpdateCampaignSuccess({
                campaign: response
              })
            ),
            catchError((error) => {
              return of(SelfDashboardActions.createOrUpdateCampaignFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );


  closeCampaign$ = createEffect(
    (selfDashboardService: SelfDashboardService = inject(SelfDashboardService)) => {
      return this.actions$.pipe(
        ofType(SelfDashboardActions.closeCampaign),
        switchMap(({ id }) =>
          selfDashboardService.closeAdherenceCampaign(id).pipe(
            map((response) =>
              SelfDashboardActions.closeCampaignSuccess({
                campaign: response
              })
            ),
            catchError((error) => {
              return of(SelfDashboardActions.closeCampaignFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  createAndSignAttestation$ = createEffect(
    (selfDashboardService: SelfDashboardService = inject(SelfDashboardService)) => {
      return this.actions$.pipe(
        ofType(SelfDashboardActions.createAndSignAttestation),
        switchMap(() =>
          selfDashboardService.createAndSignAttestation().pipe(
            map((attestation) =>
              SelfDashboardActions.createAndSignAttestationSuccess({
                attestation
              })
            ),
            catchError((error) => {
              return of(SelfDashboardActions.createAndSignAttestationFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  loadTemplate$ = createEffect(
    (selfDashboardService: SelfDashboardService = inject(SelfDashboardService)) => {
      return this.actions$.pipe(
        ofType(SelfDashboardActions.loadTemplate),
        switchMap(({ campaignId }) =>
          selfDashboardService.getTemplate(campaignId).pipe(
            map((template) =>
              SelfDashboardActions.loadTemplateSuccess({
                template
              })
            ),
            catchError((error) => {
              return of(SelfDashboardActions.loadTemplateFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  saveTemplate$ = createEffect(
    (selfDashboardService: SelfDashboardService = inject(SelfDashboardService)) => {
      return this.actions$.pipe(
        ofType(SelfDashboardActions.saveTemplate),
        switchMap(({ template }) =>
          selfDashboardService.saveTemplate(template).pipe(
            map((template) =>
              SelfDashboardActions.saveTemplateSuccess({
                template
              })
            ),
            catchError((error) => {
              return of(SelfDashboardActions.saveTemplateFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  async downloadFile(response: string, fileName: string | undefined) {
    const typeFormat = {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    };
    const link = window.document.createElement("a");
    const blob = this.base64ToBlob(response, typeFormat.type);
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName ?? "attestation_template";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }

  constructor(private actions$: Actions, private selfDashboardService: SelfDashboardService) { }
}
